import MapFeature from './MapFeature'

/**
 * Adds drag and drop to an existing map content.
 */
export default class Dragging extends MapFeature {

    /** @type {import("./MapContent")} */
    _mapContent

    /** @type {Object} */
    _draggedFeature = null

    /** @type {import("maplibre-gl").LngLat} */
    _dragOrigin = null

    /**
     * @param {import("./MapContent")} mapContent
     */
    constructor(mapContent) {
        super()

        this._mapContent = mapContent

        this._handleMouseDown = this._handleMouseDown.bind(this)
        this._handleTouchStart = this._handleTouchStart.bind(this)
        this._handleMove = this._handleMove.bind(this)
        this._handleDrop = this._handleDrop.bind(this)
    }

    _onAdd(map) {
        for (const layerId of this._mapContent.layerIds) {
            map.on('mousedown', layerId, this._handleMouseDown)
            map.on('touchstart', layerId, this._handleTouchStart)
        }
    }

    _onRemove(map) {
        for (const layerId of this._mapContent.layerIds) {
            map.off('mousedown', layerId, this._handleMouseDown)
            map.off('touchstart', layerId, this._handleTouchStart)
        }
    }

    /**
     * @param {import("maplibre-gl").MapMouseEvent} e
     */
    _handleMouseDown(e) {
        this._handleDragStart(e, 'mousemove', 'mouseup')
    }

    /**
     * @param {import("maplibre-gl").MapTouchEvent} e
     */
    _handleTouchStart(e) {
        if (e.points.length === 1) {
            this._handleDragStart(e, 'touchmove', 'touchend')
        }
    }

    /**
     * @param {{ preventDefault, features }} e
     * @param {string} moveEventType
     * @param {string} dropEventType
     */
    _handleDragStart(e, moveEventType, dropEventType) {
        e.preventDefault()
        this._draggedFeature = e.features[0]
        this._map.on(moveEventType, this._handleMove)
        this._map.once(dropEventType, this._handleDrop)
    }

    /**
     * Handle dragging a feature by updating the coordinates in the source data.
     * @param {Object} e
     */
    _handleMove(e) {
        const { lng, lat } = e.lngLat
        if (!this._dragOrigin) {
            // On first move event we only save the origin
            this._dragOrigin = e.lngLat
        } else if (this._dragOrigin.lng !== lng || this._dragOrigin.lat !== lat) {
            // On all following move events we update the source when coords differ from drag origin
            this._updateDraggedFeaturePosition(lng, lat)
        }
    }

    /**
     * @param {Object} e
     */
    _handleDrop(e) {
        // Remove move event handlers
        this._map.off('mousemove', this._handleMove)
        this._map.off('touchmove', this._handleMove)
        const { lng, lat } = e.lngLat

        if (
            this._dragOrigin &&
            (this._dragOrigin.lng !== lng || this._dragOrigin.lat !== lat)
        ) {
            // Coords differ from drag origin, so we dispatch an event
            this._draggedFeature.geometry.coordinates = [lng, lat]
            this._dispatchEvent('dragged', {
                feature: this._draggedFeature,
            })
        }

        // Back to normal mode
        this._dragOrigin = null
        this._draggedFeature = null
    }

    /**
     * Update map source to represent the dragged feature at a certain position.
     * @param {number} lng
     * @param {number} lat
     */
    _updateDraggedFeaturePosition(lng, lat) {
        this._draggedFeature.geometry.coordinates = [lng, lat]

        const data = this._mapContent.data
        for (const i in data.features) {
            const featureId = data.features[i].id
            const draggedId = this._draggedFeature.id
            if (featureId === draggedId) {
                data.features[i] = this._draggedFeature
                break
            }
        }
        this._mapContent.setData(data)
    }

}
